import { Button, Card, Divider, Group, Stack, Text } from '@mantine/core'
import { IconClock } from '@tabler/icons-react'

import type { Plan } from '@/services/api/api.types'
import { formatCostPerCredit, formatCredits, formatPlanPrice, getBillingPeriod } from '@/utils/planUtils'

interface BillingPlanCardProps {
  plan: Plan
  isSelected: boolean
  onSelect: (planId: string) => void
  disabled?: boolean
}

export function BillingPlanCard({ plan, isSelected, onSelect, disabled = false }: BillingPlanCardProps) {
  const getButtonText = (selected: boolean) => {
    if (selected) return 'Selected'
    return 'Select'
  }

  return (
    <Card
      shadow="sm"
      padding={0}
      radius="lg"
      withBorder
      miw="265px"
      maw="320px"
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        border: isSelected ? '2px solid var(--mantine-color-indigo-6)' : undefined,
        transform: isSelected ? 'translateY(-4px)' : undefined,
        transition: 'all 0.2s ease',
        flex: '1 1 200px',
      }}
      onClick={disabled ? undefined : () => onSelect(plan.bundleId)}>
      <Stack gap={0} align="center" h="100%">
        {/* Credits Header */}
        <Group align="center" gap="xs" justify="center" w="100%" py="md">
          <IconClock size={18} color={isSelected ? 'var(--mantine-color-indigo-6)' : 'var(--mantine-color-indigo-4)'} />
          <Text size="lg" fw={600} c={isSelected ? 'indigo.6' : 'indigo.4'}>
            {formatCredits(plan.creditsPerBundle)} credits
          </Text>
        </Group>

        <Divider w="100%" />

        {/* Main Content */}
        <Stack gap="sm" align="flex-start" h="100%" p="xl" w="100%" style={{ flex: 1 }}>
          <Group align="baseline" gap="xs" justify="flex-start" mt="xs">
            <Text size="48px" fw={700} c="dark" lh={1}>
              {formatPlanPrice(plan.priceDollars)}
            </Text>
            <Text size="sm" c="dimmed" fw={400}>
              {getBillingPeriod(plan, true)} {/* Always show as if auto-refill is enabled for billing period display */}
            </Text>
          </Group>

          <Text size="sm" c="dimmed" fw={400} mb="sm">
            {formatCostPerCredit(plan)}
          </Text>

          <Button
            fullWidth
            size="md"
            variant="filled"
            color={isSelected ? 'indigo.6' : 'indigo.3'}
            mt="md"
            mb="md"
            radius="md"
            disabled={disabled}
            onClick={e => {
              e.stopPropagation()
              if (!disabled) {
                onSelect(plan.bundleId)
              }
            }}>
            {getButtonText(isSelected)}
          </Button>
        </Stack>
      </Stack>
    </Card>
  )
}
